import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  QrCode,
  MapPin,
  User,
  Loader2,
  CheckCircle2,
  Copy,
  Check,
  Clock,
  ArrowLeft
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicCheckIn() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [memberId, setMemberId] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [checkInResult, setCheckInResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${API}/public/locations`);
        setLocations(response.data);
      } catch (error) {
        console.error("Error fetching locations:", error);
        toast.error("Failed to load locations");
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const handleCheckIn = async () => {
    if (!memberId.trim()) {
      toast.error("Please enter your Member ID");
      return;
    }
    if (!selectedLocation) {
      toast.error("Please select a location");
      return;
    }

    setChecking(true);
    try {
      const response = await axios.post(`${API}/public/check-in`, {
        member_id: memberId.trim(),
        location_id: selectedLocation
      });
      setCheckInResult(response.data);
      
      if (response.data.already_checked_in) {
        toast.info("You have already checked in today. Here's your code.");
      } else {
        toast.success("Check-in successful!");
      }
    } catch (error) {
      console.error("Check-in error:", error);
      toast.error(error.response?.data?.detail || "Check-in failed. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const handleCopyCode = () => {
    if (checkInResult?.code) {
      navigator.clipboard.writeText(checkInResult.code);
      setCopied(true);
      toast.success("Code copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNewCheckIn = () => {
    setCheckInResult(null);
    setMemberId("");
    setCopied(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col" data-testid="public-checkin-page">
      {/* Header */}
      <div className="p-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/login")}
          className="text-gray-600 hover:text-gray-900"
          data-testid="back-to-login-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Login
        </Button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl" data-testid="checkin-card">
          {!checkInResult ? (
            <>
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <QrCode className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl">Quick Check-In</CardTitle>
                <CardDescription>
                  Enter your Member ID to check in
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="memberId" className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    Member ID
                  </Label>
                  <Input
                    id="memberId"
                    value={memberId}
                    onChange={(e) => setMemberId(e.target.value.toUpperCase())}
                    placeholder="Enter your Member ID"
                    className="text-center text-lg font-mono"
                    data-testid="member-id-input"
                    onKeyDown={(e) => e.key === "Enter" && handleCheckIn()}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    Location
                  </Label>
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger data-testid="location-select">
                      <SelectValue placeholder="Select your location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={handleCheckIn}
                  disabled={checking || !memberId || !selectedLocation}
                  className="w-full h-12 text-lg"
                  data-testid="checkin-btn"
                >
                  {checking ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Checking In...
                    </>
                  ) : (
                    <>
                      <QrCode className="mr-2 h-5 w-5" />
                      Check In
                    </>
                  )}
                </Button>
              </CardContent>
            </>
          ) : (
            <CardContent className="p-8 text-center space-y-6">
              {/* Success/Already checked in indicator */}
              {checkInResult.already_checked_in ? (
                <>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full">
                    <Clock className="w-8 h-8 text-yellow-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">Already Checked In</h2>
                    <p className="text-gray-500">
                      {checkInResult.member_name} at {checkInResult.location_name}
                    </p>
                    <p className="text-sm text-yellow-600 mt-2">
                      You have already checked in today.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">Check-In Successful!</h2>
                    <p className="text-gray-500">
                      {checkInResult.member_name} at {checkInResult.location_name}
                    </p>
                  </div>
                </>
              )}

              {/* Code Display */}
              <div className="py-4">
                <p className="text-sm text-gray-500 mb-3">Your check-in code</p>
                
                {/* QR Code */}
                <div className="flex justify-center mb-4">
                  <div className="bg-white p-3 rounded-lg shadow-sm" data-testid="checkin-qrcode">
                    <QRCodeSVG 
                      value={checkInResult.code} 
                      size={150}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                </div>
                
                <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center gap-4">
                  <code className="text-2xl font-mono font-bold tracking-wider text-gray-900" data-testid="checkin-code">
                    {checkInResult.code}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleCopyCode}
                    data-testid="copy-code-btn"
                  >
                    {copied ? (
                      <Check className="h-5 w-5 text-green-600" />
                    ) : (
                      <Copy className="h-5 w-5" />
                    )}
                  </Button>
                </div>
              </div>

              <Button
                onClick={handleNewCheckIn}
                variant="outline"
                className="w-full"
                data-testid="new-checkin-btn"
              >
                New Check-In
              </Button>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Footer */}
      <div className="p-4 text-center text-sm text-gray-500">
        STPmembership Quick Check-In
      </div>
    </div>
  );
}
